/* ============================================================================================================
   mazo-size-required.js: NO PRESELECTED SIZE on the product page (CRO audit E1, owner OK 2026-09-29: "best UX").
   WHY: Dawn preselects the first available size (XS). On a phone the size chips sit under the sticky Add to bag /
   Buy it now bar, so shoppers bought (or abandoned) a size they never chose: XS was in 8 of 16 abandoned checkouts
   and 0 of 16 units sold (docs/research/cro-audit-2026-09-29.md).
   WHAT IT DOES:
   1. On first load, ONLY when the URL carries no ?variant= (Google Shopping / ad / shared links do, and keep their
      size), it unchecks the size chips and sets data-current-size="" on the live measurements, which shows
      "Pick a size to see its measurements" (mazo-size-live.liquid + mazo-size-required.css).
   2. Add to bag or Buy it now tapped with no size: the add is stopped, the chips scroll into view ABOVE the sticky
      bar, get the needs-a-size outline, and "Please pick your size" is announced (role=alert).
   3. The first real size pick clears everything; Dawn's own variant flow (product-info.js) takes over unchanged.
   WHY CLIENT-SIDE, NOT LIQUID: Dawn re-renders the section with ?option_values= on every pick. Rendering the chips
   unchecked in Liquid would risk the re-render clearing the shopper's own pick. Clearing once, in the browser,
   before any pick cannot touch that flow. Products with one size (or no Size option) are left alone.
   ============================================================================================================ */
(function () {
  var MSG = 'Select a size to continue';
  // Phosphor "warning-circle" (owner-supplied), sized to the text via 1em, coloured by currentColor
  var ICON = '<svg class="mazo-size-msg__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" aria-hidden="true" focusable="false"><path fill="currentColor" d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-8-80V80a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm20,36a12,12,0,1,1-12-12A12,12,0,0,1,140,172Z"/></svg>';

  function sizeRadios(root) {
    return Array.prototype.filter.call(
      root.querySelectorAll('.product-form__input--pill .mazo-optionvalues input[type="radio"]'),
      function (r) { return !r.closest('.mazo-sizelive') && !r.closest('.mazo-sizechart__units'); }
    );
  }

  function fieldsetOf(root) {
    var r = sizeRadios(root)[0];
    return r ? r.closest('fieldset') : null;
  }

  function clearState(root) {
    root.removeAttribute('data-mazo-size-unset');
    root.setAttribute('data-mazo-size-chosen', '');
    var fs = fieldsetOf(root);
    if (fs) fs.classList.remove('mazo-size-needed');
    root.querySelectorAll('.mazo-size-msg, .mazo-size-err').forEach(function (m) { m.remove(); });
  }

  function init(root) {
    if (root.__mazoSizeReq) return;
    root.__mazoSizeReq = true;
    var radios = sizeRadios(root);
    // a size came with the link, or there is nothing to choose: behave exactly like Dawn
    if (/[?&]variant=/.test(location.search) || radios.length < 2) { root.setAttribute('data-mazo-size-chosen', ''); return; }
    radios.forEach(function (r) { r.checked = false; });
    root.querySelectorAll('[data-mazo-sizelive], .mazo-sizechart').forEach(function (el) {
      el.setAttribute('data-current-size', '');
    });
    root.setAttribute('data-mazo-size-unset', '');
  }

  function scrollToChips(fs) {
    // the sticky Add to bag / Buy it now bar = the wrapper of .product-form (mazo-product.css §CTA). Only count its
    // height when it is actually pinned over the bottom of the screen (phones), so the chips land above it.
    var form = fs.closest('product-info') && fs.closest('product-info').querySelector('.product-form');
    var bar = form ? form.parentElement : null;
    var barH = 0;
    if (bar) {
      var br = bar.getBoundingClientRect();
      if (br.bottom >= window.innerHeight - 2 && br.top > window.innerHeight / 2) barH = br.height;
    }
    var top = fs.getBoundingClientRect().top + window.scrollY - (window.innerHeight - barH) / 2 + fs.offsetHeight / 2;
    if (window.mazoLenis && typeof window.mazoLenis.scrollTo === 'function') window.mazoLenis.scrollTo(Math.max(0, top));
    else window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  function askForSize(root) {
    var fs = fieldsetOf(root);
    if (!fs) return;
    fs.classList.remove('mazo-size-needed');
    void fs.offsetWidth;                                   // restart the nudge animation on repeat taps
    fs.classList.add('mazo-size-needed');
    // 2026-10-11b (owner): the error gets its own line just above the size buttons, in the strip the measurements
    // block always reserves (mazo-size-required.css), so nothing moves and the hint under cm / inch stays. Products
    // without that block keep the old line under the chips.
    var live = fs.querySelector('.mazo-sizelive');
    var m = live ? live.querySelector('.mazo-size-err') : root.querySelector('.mazo-size-msg');
    if (!m) {
      m = document.createElement('p');
      m.className = live ? 'mazo-size-err' : 'mazo-size-msg';
      m.setAttribute('role', 'alert');
      if (live) live.appendChild(m);
      else {
        var row = fs.querySelector('.mazo-optionvalues');
        (row || fs).insertAdjacentElement('afterend', m);
      }
    }
    m.innerHTML = ICON + '<span>' + MSG + '</span>';
    m.style.animation = 'none'; void m.offsetWidth; m.style.animation = '';   // restart the flash on repeat taps
    scrollToChips(fs);
    var first = sizeRadios(root).filter(function (r) { return !r.disabled; })[0];
    if (first) first.focus({ preventScroll: true });
  }

  // capture phase: runs before Dawn's product-form submit and before Shopify's Buy it now button handlers
  document.addEventListener('click', function (e) {
    var path = e.composedPath ? e.composedPath() : [e.target];
    var hit = path.some(function (n) {
      return n && n.matches && n.matches('.product-form__submit, .shopify-payment-button, .shopify-payment-button *, shopify-accelerated-checkout, shopify-buy-it-now-button');
    });
    if (!hit) return;
    var root = (e.target.closest && e.target.closest('product-info')) || document.querySelector('product-info[data-mazo-size-unset]');
    if (!root || !root.hasAttribute('data-mazo-size-unset')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    askForSize(root);
  }, true);

  document.addEventListener('change', function (e) {
    var r = e.target;
    if (!r || r.type !== 'radio' || r.closest('.mazo-sizelive') || r.closest('.mazo-sizechart__units')) return;
    var root = r.closest('product-info');
    if (root && root.hasAttribute('data-mazo-size-unset') && r.closest('.product-form__input--pill')) clearState(root);
  }, true);

  function initAll() { document.querySelectorAll('product-info').forEach(init); }
  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
})();
